// Annual market returns by index and year
// Sources: Standard & Poor's, NASDAQ, Dow Jones indices, CoinMarketCap
// Returns are total annual returns (price + dividends where applicable)

const marketReturns = {
  sp500: {
    displayName: 'S&P 500',
    emoji: '📈',
    description: 'The S&P 500 tracks 500 of the largest US companies by market cap. It\'s the most widely followed benchmark for US stock market performance.',
    longTermAvg: 10.2,  // ~10% historical average since 1926
    tenYearAvg: null,   // computed per year
    years: {
      2015: {
        return: 1.4,
        context: 'The S&P 500 barely broke even in 2015, weighed down by falling energy prices and a strong US dollar hurting multinational earnings.',
        keyEvents: ['Fed raised rates for first time since 2006', 'China market crash in August', 'Energy sector collapse'],
      },
      2016: {
        return: 12.0,
        context: 'Markets surged after the Trump election win, with financials and industrials leading the rally on expectations of deregulation and infrastructure spending.',
        keyEvents: ['Trump election rally', 'Brexit vote caused brief volatility', 'Energy sector recovery'],
      },
      2017: {
        return: 21.8,
        context: 'One of the smoothest bull market years on record — 2017 saw positive returns in every single month of the year, driven by strong earnings and the Tax Cuts and Jobs Act.',
        keyEvents: ['Tax Cuts and Jobs Act passed', 'Low volatility all year', 'Strong corporate earnings'],
      },
      2018: {
        return: -4.4,
        context: 'After a strong start, markets sold off sharply in Q4 2018 on fears of rising interest rates and US-China trade war escalation, ending the year negative.',
        keyEvents: ['Fed raised rates 4 times', 'US-China trade war began', 'December selloff: worst since 1931'],
      },
      2019: {
        return: 31.5,
        context: 'A banner year driven by Fed rate cuts (reversing 2018), a US-China trade truce, and strong consumer spending. The largest single-year gain since 1997.',
        keyEvents: ['Fed cut rates 3 times', 'Phase 1 US-China trade deal', 'Technology stocks led gains'],
      },
      2020: {
        return: 18.4,
        context: 'Despite a 34% crash in February–March due to COVID-19, massive Fed stimulus and fiscal spending drove a V-shaped recovery. Technology stocks were the big winners.',
        keyEvents: ['COVID-19 pandemic crash (-34% in 33 days)', 'Fed cut rates to 0%', '$2.2T CARES Act stimulus', 'Tech boom'],
      },
      2021: {
        return: 28.7,
        context: 'Vaccines drove reopening optimism, earnings beat expectations, and stimulus money flowed into markets. The S&P 500 hit 70 new all-time highs in 2021.',
        keyEvents: ['Vaccine rollout', '70 all-time highs', 'Meme stock frenzy (GME, AMC)', 'Inflation began rising'],
      },
      2022: {
        return: -18.1,
        context: 'The worst year for the S&P 500 since 2008. The Fed raised rates from 0% to 4.5% to fight 40-year-high inflation, crushing stocks and bonds simultaneously.',
        keyEvents: ['Fed raised rates 7 times (0% → 4.5%)', '40-year high inflation', 'Russia invades Ukraine', 'Crypto winter'],
      },
      2023: {
        return: 26.3,
        context: 'A strong rebound year led by the "Magnificent 7" AI stocks (Apple, Microsoft, NVIDIA, Alphabet, Amazon, Meta, Tesla). The AI revolution drove tech valuations higher.',
        keyEvents: ['AI boom (ChatGPT, NVIDIA)', 'Magnificent 7 dominated returns', 'Soft landing narrative', 'Banking mini-crisis (SVB)'],
      },
      2024: {
        return: 25.0,
        context: 'Another strong year as the Fed began cutting rates and AI investment accelerated. NVIDIA became the most valuable company in the world briefly.',
        keyEvents: ['Fed began cutting rates', 'AI capex boom', 'NVIDIA hit $3T market cap', 'Election uncertainty'],
      },
      2025: {
        return: -4.0,
        context: 'Markets faced headwinds from Trump tariff announcements, renewed trade war fears, and sticky inflation. Tech stocks led the decline from early-year highs.',
        keyEvents: ['Trump tariff announcements', 'Trade war fears', 'Tech selloff from ATH', 'Sticky inflation'],
      },
    },
  },

  nasdaq: {
    displayName: 'NASDAQ Composite',
    emoji: '💻',
    description: 'The NASDAQ Composite includes over 3,000 stocks, heavily weighted toward technology. It\'s more volatile than the S&P 500 but has historically offered higher long-term returns.',
    longTermAvg: 13.5,
    years: {
      2015: {
        return: 5.7,
        context: 'NASDAQ outperformed the S&P 500 in 2015, driven by strong tech earnings from Apple, Amazon, and Facebook despite broader market weakness.',
        keyEvents: ['Apple Watch launched', 'Amazon AWS growth', 'Facebook advertising surge'],
      },
      2016: {
        return: 7.5,
        context: 'Tech underperformed other sectors in 2016 as value stocks rallied post-election. NASDAQ still positive but lagged the Dow and S&P.',
        keyEvents: ['Post-election rotation to value', 'Cloud computing acceleration', 'Biotech volatility'],
      },
      2017: {
        return: 29.6,
        context: 'FAANG stocks dominated 2017 with massive gains. Netflix, Amazon, and Alphabet all surged as cloud and streaming transformed the economy.',
        keyEvents: ['FAANG stocks surged', 'Netflix/Amazon streaming wars', 'Cloud adoption accelerated'],
      },
      2018: {
        return: -3.9,
        context: 'Tech was hit hard in the Q4 2018 selloff amid concerns about slowing growth, data privacy scandals (Facebook), and rising rates.',
        keyEvents: ['Facebook Cambridge Analytica scandal', 'Rate fears hit growth stocks', 'FAANG peak-to-trough: -30%+'],
      },
      2019: {
        return: 36.7,
        context: 'NASDAQ had its best year since 2009 as rate cuts supercharged growth stocks. Apple rose 89% on the year as the services narrative caught on.',
        keyEvents: ['Apple +89%', 'Fed rate cuts boosted growth stocks', 'Netflix/streaming boom'],
      },
      2020: {
        return: 44.9,
        context: 'The pandemic supercharged the digital economy. NASDAQ surged 45% as stay-at-home beneficiaries (Zoom, Shopify, Peloton) went parabolic alongside established tech.',
        keyEvents: ['Zoom stock +400%', 'Work-from-home boom', 'Shopify, Teladoc, Peloton surged', 'Apple $2T valuation'],
      },
      2021: {
        return: 21.4,
        context: 'Another strong tech year with semiconductor shortages driving chip stocks higher. The metaverse narrative sent Meta and related stocks soaring.',
        keyEvents: ['Chip shortage boosted semis', 'Metaverse craze', 'NVIDIA hit $800B', 'Crypto + NFT boom'],
      },
      2022: {
        return: -32.5,
        context: 'The worst year for NASDAQ since 2008. Rising rates crushed high-multiple tech stocks. Meta lost 65%, Netflix fell 51%, and growth stocks broadly collapsed.',
        keyEvents: ['Meta -65%', 'Netflix -51%', 'Rising rates crushed growth multiples', 'Crypto collapse'],
      },
      2023: {
        return: 43.4,
        context: 'NASDAQ\'s best year since 1999, driven almost entirely by AI enthusiasm. NVIDIA gained 239% as investors priced in explosive AI compute demand.',
        keyEvents: ['NVIDIA +239%', 'AI gold rush', 'Magnificent 7 vs. rest', 'ChatGPT effect on cloud spending'],
      },
      2024: {
        return: 29.6,
        context: 'AI theme continued with NVIDIA remaining a dominant driver. Broadcom became a semiconductor giant while Apple struggled with China market share.',
        keyEvents: ['NVIDIA sustained AI rally', 'Broadcom joined chip boom', 'AI infrastructure buildout', 'Apple China concerns'],
      },
      2025: {
        return: -8.0,
        context: 'NASDAQ fell harder than the S&P 500 as rate uncertainty and tariff fears hit high-multiple tech names disproportionately from early-year highs.',
        keyEvents: ['Tariff fears hurt tech hardware', 'Deepseek AI shock in January', 'Rate uncertainty', 'Multiple compression'],
      },
    },
  },

  'dow-jones': {
    displayName: 'Dow Jones Industrial Average',
    emoji: '🏭',
    description: 'The Dow Jones tracks 30 large-cap US companies. It\'s the oldest and most recognized index, though it\'s price-weighted (unusual) and less diversified than the S&P 500.',
    longTermAvg: 9.4,
    years: {
      2015: {
        return: -2.2,
        context: 'The Dow was the weakest major index in 2015, dragged down by energy, materials, and industrial names that make up a larger portion of its 30 components.',
        keyEvents: ['Energy/materials drag', 'Caterpillar -27%', 'Strong dollar hurt multinationals'],
      },
      2016: {
        return: 16.5,
        context: 'The Dow surged after Trump\'s election on hopes of tax cuts and infrastructure spending. Financials and industrials — heavy in the Dow — led the rally.',
        keyEvents: ['Trump election rally', 'Goldman Sachs +32%', 'JPMorgan +25%', 'Industrials led'],
      },
      2017: {
        return: 28.1,
        context: 'The Dow crossed 20,000, 22,000, and 24,000 for the first time in 2017. Strong corporate earnings and tax reform drove record highs.',
        keyEvents: ['Dow 20k, 22k, 24k milestones', 'Tax reform passed', 'Boeing +90%'],
      },
      2018: {
        return: -5.6,
        context: 'The Dow had its worst year since 2008. Trade war fears particularly hurt industrial and consumer staple companies that dominate the 30-stock index.',
        keyEvents: ['Trade war tariffs', 'GE removed from Dow', 'December worst month in decade'],
      },
      2019: {
        return: 25.3,
        context: 'A strong rebound year for the Dow as trade war fears eased and the Fed cut rates. Apple, Microsoft, and UnitedHealth were the top contributors.',
        keyEvents: ['US-China trade truce', 'Fed rate cuts', 'Apple +85%', 'Healthcare reform fears faded'],
      },
      2020: {
        return: 9.7,
        context: 'The Dow underperformed the S&P 500 and NASDAQ due to its heavy weighting in financials, energy, and industrials that were hardest hit by the pandemic.',
        keyEvents: ['Fastest bear market in history (-37%)', 'V-shaped recovery', 'Pfizer vaccine announcement', 'Boeing -45%'],
      },
      2021: {
        return: 21.0,
        context: 'The Dow rebounded as cyclical stocks (banks, energy, industrials) recovered with the economy reopening. Goldman Sachs and Boeing were top performers.',
        keyEvents: ['Economic reopening', 'Goldman +48%', 'Caterpillar rallied', 'Infrastructure bill passed'],
      },
      2022: {
        return: -8.8,
        context: 'The Dow held up better than growth indexes in 2022 due to its value-heavy composition. Energy stocks in the index benefited from high oil prices.',
        keyEvents: ['Chevron +52% on energy boom', 'UnitedHealth +6%', 'Less growth exposure protected relative performance'],
      },
      2023: {
        return: 16.2,
        context: 'The Dow lagged the S&P 500 significantly in 2023 as AI-driven tech stocks drove most gains — and the Dow has fewer tech names.',
        keyEvents: ['Less AI exposure vs. S&P', 'Intel underperformed', 'Consumer stocks held steady'],
      },
      2024: {
        return: 15.0,
        context: 'The Dow hit 45,000 for the first time, boosted by strong corporate earnings and Fed rate cuts. NVIDIA was added to the Dow, replacing Intel.',
        keyEvents: ['Dow 45,000 record', 'NVIDIA replaced Intel in index', 'Fed rate cuts', 'Boeing bankruptcy concerns'],
      },
      2025: {
        return: -1.5,
        context: 'The Dow was more resilient than tech-heavy indexes in 2025\'s turbulent market, with defensive names and energy providing a buffer against tariff concerns.',
        keyEvents: ['Defensive stocks outperformed', 'Energy stabilized', 'Less tariff exposure vs. tech', 'Moderate volatility'],
      },
    },
  },

  bitcoin: {
    displayName: 'Bitcoin (BTC)',
    emoji: '₿',
    description: 'Bitcoin is the world\'s first and largest cryptocurrency by market cap. Its returns have been extraordinarily volatile — offering both massive gains and massive losses in any given year.',
    longTermAvg: 150.0, // rough average 2012-2024
    disclaimer: 'Past Bitcoin performance is NOT indicative of future results. Bitcoin is highly speculative, unregulated, and can lose 80%+ of its value in a single year. Not suitable for most investors.',
    years: {
      2015: {
        return: 35.2,
        context: 'Bitcoin recovered from the 2014 Mt. Gox exchange hack collapse, finding a floor around $200 and grinding higher through the year.',
        keyEvents: ['Recovery from Mt. Gox collapse', 'Price range: $150–$450', 'Early institutional interest'],
      },
      2016: {
        return: 124.6,
        context: 'Bitcoin more than doubled in 2016, driven by the halving event (reward cut in half) and growing adoption. The 2016 halving set the stage for the 2017 bull run.',
        keyEvents: ['Bitcoin halving (July 2016)', 'Bitfinex hack: -20% briefly', 'Growing merchant adoption'],
      },
      2017: {
        return: 1369.0,
        context: 'Bitcoin went from ~$1,000 to nearly $20,000 in 2017 — a 1,369% gain — fueled by retail mania, ICO boom, and mainstream media coverage.',
        keyEvents: ['$1k → $20k price run', 'ICO boom ($6.5B raised)', 'CME Bitcoin futures launched', 'Mainstream media frenzy'],
      },
      2018: {
        return: -72.6,
        context: 'The 2017 bubble burst. Bitcoin fell from nearly $20,000 to about $3,200 by year-end — a 72% loss. The ICO bubble collapsed and regulatory fears mounted.',
        keyEvents: ['$20k → $3.2k crash', 'ICO bubble burst', 'Regulatory crackdowns globally', 'Crypto winter began'],
      },
      2019: {
        return: 92.4,
        context: 'Bitcoin recovered strongly from the 2018 crash, rallying from $3,500 to nearly $14,000 by mid-year before pulling back to end around $7,200.',
        keyEvents: ['Recovery from 2018 lows', 'Libra/Facebook coin announcement boosted crypto', 'Mid-year $14k peak'],
      },
      2020: {
        return: 302.8,
        context: 'Institutional adoption kicked off Bitcoin\'s next bull run. MicroStrategy, Square, and PayPal all entered Bitcoin, sending prices from $7k to $29k by year-end.',
        keyEvents: ['MicroStrategy bought $1.1B BTC', 'PayPal enabled Bitcoin purchases', 'DeFi boom', 'Halving in May 2020'],
      },
      2021: {
        return: 59.8,
        context: 'Bitcoin hit an all-time high of ~$69,000 in November 2021 before pulling back. El Salvador made it legal tender. Coinbase went public. Institutional adoption surged.',
        keyEvents: ['ATH $69k in November', 'El Salvador Bitcoin legal tender', 'Coinbase IPO', 'Tesla bought $1.5B BTC'],
      },
      2022: {
        return: -64.3,
        context: 'Crypto winter 2.0. The collapse of Terra/Luna wiped out $40B, FTX collapsed in fraud, and rising interest rates crushed speculative assets.',
        keyEvents: ['Terra/Luna collapse ($40B wiped)', 'FTX collapse ($8B fraud)', 'Three Arrows Capital bankruptcy', '$69k → $16k'],
      },
      2023: {
        return: 154.7,
        context: 'Bitcoin staged a massive recovery, rising from $16k to $42k as BlackRock and Fidelity filed for Bitcoin ETFs, setting the stage for the 2024 bull run.',
        keyEvents: ['BlackRock Bitcoin ETF filing', 'Fidelity ETF filing', 'Banking crisis boosted BTC narrative', '$16k → $42k'],
      },
      2024: {
        return: 121.4,
        context: 'Bitcoin ETFs were approved in January 2024, unleashing institutional demand. Bitcoin hit a new all-time high of $108,000 following the Trump election.',
        keyEvents: ['Bitcoin spot ETFs approved (Jan 2024)', 'Halving in April 2024', 'ATH $108k post-election', 'MicroStrategy added to Nasdaq-100'],
      },
      2025: {
        return: -15.0,
        context: 'Bitcoin faced profit-taking from ETF-era highs and general risk-off sentiment from tariff concerns and macro uncertainty in early 2025.',
        keyEvents: ['Profit-taking from ATH', 'Strategic Bitcoin Reserve announced', 'Macro risk-off sentiment', 'Tariff volatility'],
      },
    },
  },
};

export function getMarketReturn(index, year) {
  const indexData = marketReturns[index];
  if (!indexData) return null;
  const yearNum = parseInt(year, 10);
  const yearData = indexData.years[yearNum];
  if (!yearData) return null;

  // Compute average return for this index
  const allReturns = Object.values(indexData.years).map(y => y.return);
  const tenYearAvg = (allReturns.reduce((a, b) => a + b, 0) / allReturns.length).toFixed(1);

  return { ...indexData, yearData, index, year: yearNum, tenYearAvg };
}

export const INDEXES = Object.keys(marketReturns);
export const YEARS = Array.from({ length: 11 }, (_, i) => 2015 + i);
export default marketReturns;
